/**
 * Siembra el cache de TanStack Query con los datos de vista previa, para que
 * las pantallas reales de Tours rendericen sin tocar el backend (SOLO preview).
 */
import { queryClient } from '../lib/query-client'
import { toursQueryKeys } from '../lib/hooks/use-tours'
import { useToursCheckoutStore } from '../lib/stores/tours-checkout.store'
import { MIN_TOUR_PASSENGERS } from '../lib/tours/booking-rules'
import { previewCreateSplit, previewSplitInfo } from '../lib/tours/preview-payments'
import { previewCatalog, previewExperience } from './data'

export function seedToursPreview(): void {
  // La primera experiencia lleva rating + reseñas para poder ver la UI completa.
  const catalogWithRating = previewCatalog.map((item, index) =>
    index === 0 ? { ...item, ratingAvg: 4.5, ratingCount: 2 } : item,
  )
  queryClient.setQueryData(toursQueryKeys.catalog('SV'), catalogWithRating)
  // Los demás países están visibles para mantener la navegación regional,
  // pero aún no tienen inventario mientras Tago opera únicamente en SV.
  for (const country of ['BZ', 'CR', 'GT', 'HN', 'NI', 'PA']) {
    queryClient.setQueryData(toursQueryKeys.catalog(country), [])
  }
  for (const item of catalogWithRating) {
    const detail = previewExperience(item.id)
    if (!detail) continue
    queryClient.setQueryData(
      toursQueryKeys.experience(item.id),
      item.ratingCount
        ? { ...detail, ratingAvg: item.ratingAvg, ratingCount: item.ratingCount }
        : detail,
    )
  }

  const first = previewCatalog[0]
  if (first) {
    queryClient.setQueryData(toursQueryKeys.reviews(first.id), [
      {
        id: 'tourreview_preview_1',
        rating: 5,
        comment:
          'Espectacular: los cafetales, los murales de Ataco y la comida en Juayúa. Muy bien organizado.',
        createdAt: new Date(Date.now() - 3 * 24 * 3600 * 1000).toISOString(),
      },
      {
        id: 'tourreview_preview_2',
        rating: 4,
        comment: 'Excelente día. La van cómoda y el guía muy atento.',
        createdAt: new Date(Date.now() - 9 * 24 * 3600 * 1000).toISOString(),
      },
    ])
  }
}

/** Contexto de checkout para poder ver la pantalla de pago sin backend. */
export function seedToursCheckout(): void {
  const experience = previewCatalog[0]
  if (!experience) return
  const vehicle = experience.vehicles[0]
  const startsAt = new Date(Date.now() + 10 * 24 * 3600 * 1000).toISOString()
  useToursCheckoutStore.getState().setContext({
    booking: {
      id: 'tourbook_preview',
      departureId: 'departure_preview',
      organizerId: 'preview',
      passengerCount: MIN_TOUR_PASSENGERS,
      cargoM3: 0.9,
      luggageSpec: ['beach', 'beach'],
      totalCents: vehicle?.guestPriceCents ?? 20000,
      status: 'capacity_held',
      guestPriceCents: vehicle?.guestPriceCents,
      startsAt,
      unitId: vehicle?.unitId,
      holdExpiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    },
    title: experience.title,
    originLabel: experience.originLabel,
    destinationLabel: experience.destinationLabel,
    startsAt,
    luggageLabels: ['Playa', 'Playa'],
    coverImageUrl: experience.coverImageUrl,
  })
}

/** Perfil de host + ganancias para ver la pantalla sin backend. */
export function seedToursHost(): void {
  queryClient.setQueryData(toursQueryKeys.host, {
    host: {
      id: 'tourhost_preview',
      displayName: 'Tago',
      businessName: 'Tago Rent',
      rntRegistrationNumber: 'RNT-123',
      vmtTourismPermitNumber: null,
      insurancePolicyNumber: null,
      insuranceExpiresAt: null,
      verificationStatus: 'verified',
      isOperational: true,
    },
    units: [
      {
        id: 'tourunit_preview',
        label: 'Microbús',
        makeModel: 'Microbús 15 pasajeros',
        licensePlate: 'MI-0001',
        seats: 15,
        cargoVolumeM3: 1.5,
        status: 'active',
      },
    ],
  })
  queryClient.setQueryData(toursQueryKeys.hostPayouts, {
    balance: { earnedCents: 43425, disbursedCents: 20000, availableCents: 23425 },
    payouts: [],
  })
  queryClient.setQueryData(toursQueryKeys.hostDepartures, {
    experiences: [
      { id: 'tourexp_ruta_de_las_flores', title: 'Ruta de las Flores', durationMinutes: 600 },
      { id: 'tourexp_ruta_los_volcanes', title: 'Ruta Los Volcanes', durationMinutes: 600 },
    ],
    departures: [
      {
        id: 'dep_preview_1',
        experienceId: 'tourexp_ruta_de_las_flores',
        startsAt: new Date(Date.now() + 10 * 24 * 3600 * 1000).toISOString(),
        endsAt: new Date(Date.now() + 10 * 24 * 3600 * 1000 + 600 * 60000).toISOString(),
        seatsCapacity: 15,
        cargoVolumeM3: 1.5,
        status: 'open',
      },
    ],
  })
  queryClient.setQueryData(toursQueryKeys.hostBookings, [
    {
      id: 'hostbook_preview_1',
      status: 'paid',
      passengerCount: MIN_TOUR_PASSENGERS,
      totalCents: 20000,
      startsAt: new Date(Date.now() + 10 * 24 * 3600 * 1000).toISOString(),
      title: 'Ruta de las Flores',
    },
    {
      id: 'hostbook_preview_2',
      status: 'in_progress',
      passengerCount: MIN_TOUR_PASSENGERS,
      totalCents: 19000,
      startsAt: new Date(Date.now() + 2 * 24 * 3600 * 1000).toISOString(),
      title: 'Ruta Los Volcanes',
    },
  ])
}

/** Info de una parte de split para ver la pantalla pública sin backend. */
export function seedToursSplit(token: string): void {
  seedToursCheckout()
  const experience = previewCatalog[0]
  const context = useToursCheckoutStore.getState().context
  const totalCents = context?.booking.totalCents ?? 20000
  const startsAt = context?.startsAt ?? new Date(Date.now() + 10 * 24 * 3600 * 1000).toISOString()
  previewCreateSplit('tourbook_preview', MIN_TOUR_PASSENGERS, totalCents, {
    title: experience?.title ?? 'Ruta de las Flores',
    originLabel: experience?.originLabel ?? 'San Salvador',
    destinationLabel: experience?.destinationLabel ?? 'Apaneca · Ataco · Juayúa',
    startsAt,
  })
  const info = previewSplitInfo(token) ?? previewSplitInfo('preview-share-01')
  if (info) {
    queryClient.setQueryData(['tours', 'split', token], info)
  }
}
export function seedToursBookings(): void {
  const experience = previewCatalog[0]
  const future = new Date(Date.now() + 10 * 24 * 3600 * 1000).toISOString()
  const past = new Date(Date.now() - 5 * 24 * 3600 * 1000).toISOString()
  queryClient.setQueryData(toursQueryKeys.bookings, [
    {
      id: 'tourbook_1',
      status: 'paid',
      passengerCount: MIN_TOUR_PASSENGERS,
      totalCents: experience?.vehicles[0]?.guestPriceCents ?? 20000,
      createdAt: past,
      startsAt: future,
      title: experience?.title ?? 'Ruta de las Flores',
      originLabel: 'San Salvador',
      destinationLabel: 'Apaneca · Ataco · Juayúa',
      refundable: true,
    },
    {
      id: 'tourbook_2',
      status: 'cancelled',
      passengerCount: MIN_TOUR_PASSENGERS,
      totalCents: 38000,
      createdAt: past,
      startsAt: past,
      title: 'Ruta Los Volcanes',
      originLabel: 'San Salvador',
      destinationLabel: 'Cerro Verde · Volcán de Izalco',
      refundable: false,
    },
  ])
}

/** Operación compartida por todos los pasajeros de una salida. */
export function seedToursTrip(
  bookingId: string,
  viewerRole: 'organizer' | 'operator' = 'organizer',
): void {
  const experience = previewExperience(previewCatalog[0]?.id ?? '')
  if (!experience) return
  const now = new Date()
  const isUnassignedPreview = viewerRole === 'operator' && bookingId.includes('unassigned')
  const startsAt = new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString()
  const endsAt = new Date(now.getTime() + 5 * 60 * 60 * 1000).toISOString()
  const assignedDriverId = '00000000-0000-4000-8000-000000000001'
  const snapshot = {
    viewerRole,
    booking: {
      id: bookingId,
      status: viewerRole === 'operator' ? ('paid' as const) : ('in_progress' as const),
      passengerCount: MIN_TOUR_PASSENGERS,
    },
    departure: { id: 'tourdep_preview_live', startsAt, endsAt },
    experience: {
      id: experience.id,
      title: experience.title,
      originLabel: experience.originLabel,
      destinationLabel: experience.destinationLabel,
      routeGeojson: null,
    },
    stops: experience.stops,
    run: {
      id: 'tourrun_preview_live',
      departureId: 'tourdep_preview_live',
      driverId: viewerRole === 'operator' && !isUnassignedPreview ? assignedDriverId : null,
      status: viewerRole === 'operator' ? ('scheduled' as const) : ('in_progress' as const),
      currentStopPosition: 1,
      etaAt: new Date(now.getTime() + 45 * 60 * 1000).toISOString(),
      emergencyStatus: 'none' as const,
      lastLocationAt: now.toISOString(),
      startedAt: startsAt,
      arrivedAt: null,
      completedAt: null,
      updatedAt: now.toISOString(),
    },
    latestLocation: experience.stops[1]
      ? {
          latitude: experience.stops[1].latitude + 0.006,
          longitude: experience.stops[1].longitude - 0.004,
          accuracyM: 12,
          headingDeg: 282,
          speedMps: 14,
          capturedAt: now.toISOString(),
        }
      : null,
    driver: isUnassignedPreview
      ? null
      : {
          displayName: 'Andrea Martínez',
          avatarUrl: null,
          verified: true,
        },
    guardian: {
      configured: true,
      includesMinors: false,
      responsibleAdultName: 'Carlos',
      consentedAt: now.toISOString(),
      activeShare: {
        guardianName: 'María',
        expiresAt: new Date(Date.parse(endsAt) + 6 * 60 * 60 * 1000).toISOString(),
        createdAt: now.toISOString(),
      },
    },
    operator:
      viewerRole === 'operator'
        ? {
            canAssignDriver: true,
            availableDrivers: [
              {
                id: assignedDriverId,
                displayName: 'Andrea Martínez',
                avatarUrl: null,
                verified: true,
                operatorRole: 'driver' as const,
                licenseExpiresAt: '2028-04-30',
              },
              {
                id: '00000000-0000-4000-8000-000000000002',
                displayName: 'Mateo Hernández',
                avatarUrl: null,
                verified: true,
                operatorRole: 'driver' as const,
                licenseExpiresAt: '2027-11-18',
              },
            ],
          }
        : null,
  }
  queryClient.setQueryData(toursQueryKeys.trip(bookingId), snapshot)
}

export function seedToursGuardian(token: string): void {
  const bookingId = 'tourbook_preview_guardian'
  seedToursTrip(bookingId)
  const trip = queryClient.getQueryData(toursQueryKeys.trip(bookingId)) as
    | Record<string, unknown>
    | undefined
  if (!trip) return
  const publicSnapshot = { ...trip }
  delete publicSnapshot.viewerRole
  delete publicSnapshot.guardian
  delete publicSnapshot.operator
  queryClient.setQueryData(toursQueryKeys.guardian(token), {
    ...publicSnapshot,
    guardianName: 'María',
    expiresAt: new Date(Date.now() + 11 * 60 * 60 * 1000).toISOString(),
  })
}
